/**
 * A typed wrapper around Map that preserves generic type information when used with Vue's reactive().
 *
 * Vue's reactive() applies UnwrapNestedRefs to stored values, which breaks type inference for
 * Map methods like get(), values(), etc. This wrapper class shields the internal Map from Vue's
 * type transformation, preserving full type safety without needing 'as' casts.
 *
 * @example
 * // Instead of:
 * const markers = reactive(new Map<string, Marker>());
 * const marker = markers.get(id) as Marker | undefined; // Cast required
 *
 * // Use:
 * const markers = reactive(new TypedReactiveMap<string, Marker>());
 * const marker = markers.get(id); // Fully typed, no cast needed
 */
export class TypedReactiveMap<K, V> {
  private readonly map = new Map<K, V>();

  constructor(initialEntries?: Iterable<[K, V]> | null) {
    if (initialEntries) {
      for (const [key, value] of initialEntries) {
        this.map.set(key, value);
      }
    }
  }

  get(key: K): V | undefined {
    return this.map.get(key);
  }

  set(key: K, value: V): this {
    this.map.set(key, value);
    return this;
  }

  has(key: K): boolean {
    return this.map.has(key);
  }

  delete(key: K): boolean {
    return this.map.delete(key);
  }

  clear(): void {
    this.map.clear();
  }

  entries(): MapIterator<[K, V]> {
    return this.map.entries();
  }

  keys(): MapIterator<K> {
    return this.map.keys();
  }

  values(): MapIterator<V> {
    return this.map.values();
  }

  get size(): number {
    return this.map.size;
  }

  forEach(callback: (value: V, key: K, map: Map<K, V>) => void): void {
    this.map.forEach(callback);
  }
}
